<template>
    <v-card id="funding" elevation="4" class="mb-8">
        <v-toolbar color="green darken-4" dark flat dense>
            <v-toolbar-title>Your Full-Time Education (FE) Funding</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn v-scroll-to="'#top'" dark text>
                <v-icon left>fas fa-chevron-up</v-icon>
                Top
            </v-btn>
            <span v-if="HasHNEnrolments || HasBeingProcessedBursaryApplications" v-scroll-to="'#kickers'" class="fa-stack fa-md icon-info">
                <i class="fa fa-circle fa-stack-2x"></i>
                <i class="fa fa-info fa-stack-1x"></i>
            </span>
            <span v-else-if="HasUnstartedBursaryApplications" v-scroll-to="'#kickers'" class="fa-stack fa-md icon-error">
                <i class="fa fa-circle fa-stack-2x"></i>
                <i class="fa fa-exclamation fa-stack-1x"></i>
            </span>
            <span v-else-if="HasUnsubmittedBursaryApplications" v-scroll-to="'#kickers'" class="fa-stack fa-md icon-warning">
                <i class="fa fa-circle fa-stack-2x"></i>
                <i class="fa fa-info fa-stack-1x"></i>
            </span>
            <span v-else v-scroll-to="'#kickers'" class="fa-stack fa-md icon-success">
                <i class="fa fa-circle fa-stack-2x"></i>
                <i class="fa fa-check fa-stack-1x"></i>
            </span>
        </v-toolbar>
        <v-card-text>
            <v-alert v-if="CurrentDocumentProcessingDate != null" type="info" outlined dense text>
                We are currently processing Bursary/EMA documents uploaded on <strong>{{ CurrentDocumentProcessingDate | formatLongDate }}</strong>
            </v-alert>
            <p>There are two main funds which provide further education (also called non-advanced) student assistance with living expenses.</p>
            <p>These are the Education Maintenance Allowance (EMA) for students who are under the age of 18 years at the official start date of their course, and the Further Education Bursary Fund for all other students at non-advanced level.</p>
            <h4>The courses below are then the ones you have applied for that fall into this category of funding:</h4>
        </v-card-text>
        <v-card-text>
            <v-card v-for="enrolment in FEEnrolments" :key="enrolment.EnrolmentID" class="mb-4" outlined>
                <v-card-title>
                    <span class="body-1">{{ enrolment.CourseCode }} - {{ enrolment.CourseName }}</span>
                    <v-spacer></v-spacer>
                    <v-btn v-if="IsBursaryApplicationUnstarted(enrolment.EnrolmentID)" :href="FundingLink(enrolment.EnrolmentID)" target="_blank" color="error" dark small style="color:white !important">
                        <v-icon left small>fas fa-pencil-alt</v-icon>
                        Begin your Funding Application
                    </v-btn>
                    <v-btn v-else-if="IsBursaryApplicationBeingProcessed(enrolment.EnrolmentID) || IsBursaryApplicationCompleted(enrolment.EnrolmentID)" :href="FundingLink(enrolment.EnrolmentID)" target="_blank" color="info" dark small style="color:white !important">
                        <v-icon left small>fas fa-check</v-icon>
                        View your Funding Application
                    </v-btn>
                    <v-btn v-else-if="IsBursaryApplicationUnsubmitted(enrolment.EnrolmentID)" :href="FundingLink(enrolment.EnrolmentID)" target="_blank" color="error" dark small style="color:white !important">
                        <v-icon left small>fas fa-cogs</v-icon>
                        Continue your Funding Application
                    </v-btn>
                </v-card-title>
            </v-card>
        </v-card-text>
        <v-card-actions>
            <v-btn href="https://heretohelp.dundeeandangus.ac.uk/here-to-help-app-fund/funding/further-education-funding/" target="_blank" color="info" dark small style="color:white !important">
                <v-icon left small>fas fa-info-circle</v-icon>
                FE Funding Information
            </v-btn>
        </v-card-actions>
    </v-card>
</template>

<script>
    import filtersMixin from '@/views/components/mixins/base/filters.js'
    import enrolmentsMixin from '@/views/components/mixins/enterpriseDataApi/enrolments.js'

    export default {
        props: ['studentFundingLocalSettings', 'enrolments', 'bursaryApplications'],
        mixins: [
            filtersMixin,
            enrolmentsMixin,
        ],
        computed: {
            CurrentDocumentProcessingDate() { return this.studentFundingLocalSettings?.KeyValuePairs?.CurrentDocumentProcessingDate; },
            FEBursaryApplications() { return this.bursaryApplications.filter(ba => this.FEEnrolments.filter(e => e.EnrolmentID == ba.EnrolmentID)); },
            HasUnstartedBursaryApplications() {
                return this.FEBursaryApplications.length == 0 || this.FEBursaryApplications.filter(ba => ba.ApplicationStatusCode == null).length > 0;
            },
            HasUnsubmittedBursaryApplications() {
                return this.FEBursaryApplications.length > 0 && this.FEBursaryApplications.filter(ba => ['UNSUB', 'DC'].includes(ba.ApplicationStatusCode) === true).length > 0;
            },
            HasBeingProcessedBursaryApplications() {
                return this.FEBursaryApplications.length > 0 && this.FEBursaryApplications.filter(ba => ['SUB'].includes(ba.ApplicationStatusCode) === true).length > 0;
            },
            HasCompletedBursaryApplications() {
                return this.FEBursaryApplications.length > 0 && this.FEBursaryApplications.filter(ba => ['ADCHK', 'FINAL'].includes(ba.ApplicationStatusCode) === true).length > 0;
            },
        },
        methods: {
            BursaryApplication(enrolmentID) { return this.bursaryApplications.find(ba => ba.EnrolmentID == enrolmentID); },
            BursaryApplicationStatus(enrolmentID) { return this.BursaryApplication(enrolmentID)?.ApplicationStatusCode; },
            IsBursaryApplicationUnstarted(enrolmentID) {
                return this.BursaryApplicationStatus(enrolmentID) == null;
            },
            IsBursaryApplicationUnsubmitted(enrolmentID) {
                return ['UNSUB', 'DC'].includes(this.BursaryApplicationStatus(enrolmentID)) === true;
            },
            IsBursaryApplicationBeingProcessed(enrolmentID) {
                return ['SUB'].includes(this.BursaryApplicationStatus(enrolmentID)) === true;
            },
            IsBursaryApplicationCompleted(enrolmentID) {
                return ['ADCHK', 'FINAL'].includes(this.BursaryApplicationStatus(enrolmentID)) === true;
            },
            FundingLink(enrolmentID) {
                return this.enrolments.find(x => x.EnrolmentID == enrolmentID)?.AcademicSession >= 2026
                    ? 'https://studentfunding.dundeeandangus.ac.uk/login'
                    : 'https://cams.dundeeandangus.ac.uk/CAMS/UserSecurity/Login.aspx?ReturnUrl=%2fCAMS%2fDefault.aspx'
            }
        },
    }
</script>
